// Bid-readiness scorecard. Evaluates an estimate across project / cost / resourcing /
// BREX / applicability dimensions and returns a list of checks plus an overall grade.
import { BR_CATEGORY_DM_MAP } from "./s1000dData";

const PASS = "pass";
const WARN = "warn";
const FAIL = "fail";

const aggregate = (checks) => {
  if (checks.some((c) => c.status === FAIL)) return FAIL;
  if (checks.some((c) => c.status === WARN)) return WARN;
  return PASS;
};

export function computeReadiness(estimate, totals, resourcing, brex, risk) {
  const checks = [];
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  // --- Project setup ---
  checks.push({
    id: "project-name",
    dimension: "Project",
    label: "Project name",
    status: estimate.projectName?.trim() ? PASS : FAIL,
    message: estimate.projectName?.trim()
      ? `“${estimate.projectName}”`
      : "Set a project name before submitting the bid.",
  });
  checks.push({
    id: "tags",
    dimension: "Project",
    label: "Tags / classification",
    status: (estimate.tags?.length || 0) > 0 ? PASS : WARN,
    message: (estimate.tags?.length || 0) > 0
      ? `${estimate.tags.length} tag(s) applied`
      : "Add at least one tag (e.g. defense, aerospace) for portfolio tracking.",
  });

  // --- Cost & estimation ---
  checks.push({
    id: "has-dms",
    dimension: "Estimation",
    label: "Data modules in scope",
    status: totals.totalDms > 0 ? PASS : FAIL,
    message: totals.totalDms > 0
      ? `${totals.totalDms} DM(s), ${totals.totalIllus} illustration(s)`
      : "Estimate has zero data modules.",
  });

  const effortSum = (estimate.roles || []).reduce((s, r) => s + n(r.effortPercent), 0);
  checks.push({
    id: "role-effort-100",
    dimension: "Estimation",
    label: "Role effort allocation",
    status: Math.abs(effortSum - 100) < 0.01 ? PASS : FAIL,
    message: `Sum of role effort % = ${effortSum.toFixed(1)}%${Math.abs(effortSum - 100) < 0.01 ? "" : " (must equal 100%)"}`,
  });

  const oh = n(estimate.overheadPercent);
  checks.push({
    id: "overhead",
    dimension: "Estimation",
    label: "Overhead %",
    status: oh > 50 ? FAIL : (oh === 0 ? WARN : PASS),
    message: oh === 0 ? "Overhead is zero — bids usually include some overhead." :
             oh > 50 ? `Overhead ${oh}% is unusually high.` :
             `Overhead set to ${oh}%.`,
  });
  const cont = n(estimate.contingencyPercent);
  checks.push({
    id: "contingency",
    dimension: "Estimation",
    label: "Contingency %",
    status: cont > 30 ? WARN : (cont === 0 ? WARN : PASS),
    message: cont === 0 ? "No contingency reserved." :
             cont > 30 ? `Contingency ${cont}% is high — confirm with PM.` :
             `Contingency set to ${cont}%.`,
  });

  checks.push({
    id: "blended-rate",
    dimension: "Estimation",
    label: "Blended hourly rate",
    status: totals.blendedRate > 0 ? PASS : FAIL,
    message: `Blended rate ${totals.blendedRate.toFixed(2)} ${estimate.currency?.code || "USD"}/hr`,
  });

  // --- Resourcing ---
  const datesSet = !!(estimate.resourcePlan?.startDate && estimate.resourcePlan?.endDate);
  checks.push({
    id: "dates-set",
    dimension: "Schedule",
    label: "Project window",
    status: datesSet ? PASS : WARN,
    message: datesSet
      ? `${estimate.resourcePlan.startDate} → ${estimate.resourcePlan.endDate} (${resourcing.workingDays} working days)`
      : "Start and end dates not yet set.",
  });
  if (datesSet) {
    checks.push({
      id: "working-days",
      dimension: "Schedule",
      label: "Working days available",
      status: resourcing.workingDays > 0 ? PASS : FAIL,
      message: resourcing.workingDays > 0
        ? `${resourcing.workingDays} working days`
        : "Project window has 0 working days — check dates / holidays.",
    });
  }
  const fte = resourcing.fteEffective || 0;
  checks.push({
    id: "fte-feasible",
    dimension: "Schedule",
    label: "FTE feasibility",
    status: fte > 20 ? FAIL : fte > 10 ? WARN : PASS,
    message: `Required ${fte.toFixed(2)} FTE${fte > 20 ? " — likely needs sub-contracting or schedule extension." : fte > 10 ? " — large team, plan ramp-up carefully." : "."}`,
  });
  const stdHrs = n(estimate.resourcePlan?.standardHoursPerDay);
  const effHrs = n(estimate.resourcePlan?.effectiveHoursPerDay);
  checks.push({
    id: "hours-ratio",
    dimension: "Schedule",
    label: "Effective vs standard hours",
    status: effHrs > 0 && effHrs <= stdHrs ? PASS : WARN,
    message: effHrs > stdHrs
      ? `Effective ${effHrs}h > standard ${stdHrs}h — utilisation > 100% is unrealistic.`
      : effHrs === 0
        ? "Effective hours not set."
        : `Utilisation ${((effHrs / Math.max(1, stdHrs)) * 100).toFixed(0)}%`,
  });

  // --- BREX coverage ---
  const usedDmCodes = new Set();
  for (const code of Object.keys(estimate.dmMatrix || {})) {
    const r = estimate.dmMatrix[code];
    const q = n(r?.simple?.qty) + n(r?.intermediate?.qty) + n(r?.complex?.qty);
    if (q > 0) usedDmCodes.add(code);
  }
  let relevantCats = 0;
  let relevantIncluded = 0;
  for (const cat of brex.categoryRows) {
    const mapped = BR_CATEGORY_DM_MAP[cat.id] || [];
    const isUniversal = mapped.includes("*");
    const intersect = mapped.some((c) => c !== "*" && usedDmCodes.has(c));
    const relevant = isUniversal || intersect;
    if (relevant) {
      relevantCats += 1;
      if (cat.included) relevantIncluded += 1;
    }
  }
  const coverage = relevantCats > 0 ? (relevantIncluded / relevantCats) * 100 : 0;
  checks.push({
    id: "brex-coverage",
    dimension: "BREX",
    label: "Relevant-category coverage",
    status: coverage >= 80 ? PASS : coverage >= 50 ? WARN : FAIL,
    message: `${relevantIncluded} of ${relevantCats} DM-relevant BR categories included (${coverage.toFixed(0)}%)`,
  });

  const brexEffortSum = (estimate.brexPlan?.roles || []).reduce((s, r) => s + n(r.effortPercent), 0);
  checks.push({
    id: "brex-roles-100",
    dimension: "BREX",
    label: "BREX role effort allocation",
    status: Math.abs(brexEffortSum - 100) < 0.01 ? PASS : WARN,
    message: `Sum of BREX role effort % = ${brexEffortSum.toFixed(1)}%`,
  });

  // --- Applicability completeness ---
  const actDms = ["ACT", "PCT", "CCT"].reduce((s, code) => {
    const r = estimate.dmMatrix?.[code];
    return s + n(r?.simple?.qty) + n(r?.intermediate?.qty) + n(r?.complex?.qty);
  }, 0);
  checks.push({
    id: "applicability",
    dimension: "Applicability",
    label: "Applicability cross-reference tables",
    status: actDms > 0 ? PASS : WARN,
    message: actDms > 0
      ? `${actDms} ACT / PCT / CCT module(s) planned`
      : "No ACT/PCT/CCT data modules in scope — confirm applicability strategy.",
  });

  const overall = aggregate(checks);

  // --- Risk register ---
  if (risk) {
    checks.push({
      id: "risk-register",
      dimension: "Risk",
      label: "Risk register populated",
      status: risk.count >= 3 ? PASS : risk.count >= 1 ? WARN : FAIL,
      message: risk.count > 0
        ? `${risk.count} risk(s) logged · ${risk.breakdown.critical} critical, ${risk.breakdown.high} high`
        : "No risks logged — bid lacks risk assessment.",
    });
    checks.push({
      id: "risk-exposure",
      dimension: "Risk",
      label: "Risk exposure",
      status: risk.breakdown.critical > 0 ? FAIL : risk.breakdown.high > 0 ? WARN : PASS,
      message: risk.breakdown.critical > 0
        ? `${risk.breakdown.critical} critical risk(s) — mitigation required before bid.`
        : risk.breakdown.high > 0
          ? `${risk.breakdown.high} high risk(s) — review mitigation plans.`
          : `Max risk score ${risk.maxScore}/25 — acceptable exposure.`,
    });
    checks.push({
      id: "assumptions",
      dimension: "Risk",
      label: "Assumptions documented",
      status: risk.assumptionsCount >= 2 ? PASS : risk.assumptionsCount >= 1 ? WARN : FAIL,
      message: `${risk.assumptionsCount} assumption(s) logged`,
    });
  }
  const counts = {
    pass: checks.filter((c) => c.status === PASS).length,
    warn: checks.filter((c) => c.status === WARN).length,
    fail: checks.filter((c) => c.status === FAIL).length,
    total: checks.length,
  };
  const score = Math.round((counts.pass / counts.total) * 100);
  const finalOverall = aggregate(checks);
  return { checks, overall: finalOverall, counts, score, coverage };
}
